// Types et petits composants partagés par les écrans « Nomenclature et devis ».
import { useEffect, useRef, useState, useSyncExternalStore, type InputHTMLAttributes } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '../../components/ui.tsx';
import { date, euros } from '../../lib/format.ts';

export const SECTIONS = ['materiel', 'accessoire', 'licence', 'pose', 'cablage', 'programmation', 'mise_en_service', 'essais',
  'documentation', 'deplacement', 'sous_traitance', 'frais', 'autre'] as const;
export type Section = typeof SECTIONS[number];
export const LIBELLES_SECTIONS: Record<Section, string> = {
  materiel: 'Matériel', accessoire: 'Accessoires', licence: 'Licences', pose: 'Pose', cablage: 'Câblage',
  programmation: 'Programmation', mise_en_service: 'Mise en service', essais: 'Essais', documentation: 'Documentation',
  deplacement: 'Déplacements', sous_traitance: 'Sous-traitance', frais: 'Frais', autre: 'Autres postes',
};
export const OPTIONS_SECTIONS = SECTIONS.map((s) => ({ valeur: s, libelle: LIBELLES_SECTIONS[s] }));

export const LIBELLES_MOTIFS: Record<string, string> = {
  cout_unitaire: 'Coût unitaire à renseigner',
  heures_a_renseigner: 'Temps de pose à renseigner',
  taux_horaire_absent: 'Taux horaire à choisir pour les heures saisies',
  taux_horaire_inconnu: 'Taux horaire introuvable ou inactif',
  cout_horaire_inconnu: 'Coût horaire du taux à renseigner',
  taux_marge_invalide: 'Taux de marge égal ou supérieur à 100 %',
  prix_a_fixer_direction: 'À fixer par la direction',
};

/** Ma gamme, prix imposé aux comptes sans accès achats (décision du 01/11/2026). */
export const LIBELLE_A_FIXER = 'À fixer par la direction';
export const LIBELLE_SOUS_TARIF = 'Sous le tarif — accord de la direction';
export const LIBELLE_REMISE_GAMME = 'Remise sur du matériel Ma gamme — accord de la direction';
/** État du prix d'une ligne Ma gamme : tarif du produit, prix à fixer, baisse sous le tarif, accord (jamais le coût ni le taux). */
export interface PrixGamme {
  tarif: string | null; a_fixer: boolean; sous_tarif: boolean;
  /** Accord valable pour ce prix ET cette quantité (un prix consenti l'est pour une quantité). */
  accord: { par: string | null; le: string; prix: string; quantite?: string } | null;
  /** Ligne libre reconnue comme produit Ma gamme par sa référence. */
  reconnue?: boolean;
  /** Auteur du prix en attente (prix saisi par un compte sans accès achats). */
  saisi_par?: string | null;
}
/** Remise globale dont l'assiette contient du matériel Ma gamme (01/11/2026) : accord de la direction nécessaire à l'émission. */
export interface RemiseGamme {
  requis: boolean; pct: string; scope: 'devis' | 'ma_gamme';
  accord: { par: string | null; le: string; pct: string } | null;
  saisie_par: string | null;
  /** Empreinte de la part Ma gamme de l'assiette : servie aux seuls comptes gamme.achats, renvoyée avec l'accord (valeur vue). */
  empreinte?: string;
}

export interface Calcul {
  id: string; complet: boolean; a_renseigner: string[];
  cout_materiel?: string | null; cout_mo?: string | null; cout_horaire?: string | null; cout_revient?: string | null;
  /** null : ligne Ma gamme d'un compte sans accès achats (taux réservé, 01/11/2026). */
  taux_applique?: string | null; taux_origine?: 'ligne' | 'revision' | null;
  prix_unitaire_ht: string | null; total_ht: string | null; marge?: string | null; marge_pct?: string | null; majoration_pct?: string | null;
  /** Prix de vente unitaire HT convenu (document signé ou commandé) : le prix ne dépend pas du coût. */
  prix_convenu?: string | null; cout_a_renseigner?: string[];
  /** Frais d'approche du fournisseur compris dans le coût matériel (option tarification avancée). */
  frais_approche?: { pct: string; montant: string | null };
  /** Coût réel et marge réelle (administrateur seulement, quand l'option est active). */
  reel?: { cout_materiel?: string | null; cout_mo?: string | null; cout_revient: string | null; marge: string | null; marge_pct: string | null };
}
export interface Ligne {
  id: string; position: number; section: Section; label: string; manufacturer: string | null; reference: string | null;
  quantity: string; unit: string; unit_cost?: string | null; cost_source?: string | null; cost_date?: string | null;
  labor_hours: string | null; labor_rate_code?: string | null; rate_override?: string | null; requirement_id: string | null;
  requirement_label?: string | null; price_item_id?: string | null; calcul: Calcul;
  sale_unit_price?: string | null;
  /** Source de la quantité (chiffrage proposé : ligne du DPGF, comptage du CCTP, métré, saisie à la main) ; null = inconnue. */
  quantity_source?: string | null;
  /** Ligne reprise d'un produit « Ma gamme » (option) : signalée en interne, jamais imprimée. */
  gamme_produit_id?: string | null;
  /** Ligne Ma gamme servie sans prix d'achat (compte sans accès achats, décision du 31/10/2026) : coût et marge « réservés ». */
  achat_reserve?: boolean;
  /** Surplus intégré au devis (22/10/2026) : besoin net et part de surplus de la quantité vendue. */
  net_quantity?: string | null; surplus_quantity?: string | null;
  /** Administrateur, option active : coût unitaire affiché aux autres rôles. */
  unit_cost_affiche?: string | null;
  /** Ma gamme : état du prix imposé (absent pour le technicien et hors Ma gamme). */
  prix_gamme?: PrixGamme;
  /** Posé à l'écran (normaliserVue) : ligne Ma gamme servie SANS prix ni calcul (technicien, 3e passe du 01/11/2026). */
  prix_reserve?: boolean;
}

/** Ma gamme (3e passe, I1) : un compte sans devis.couts ni prix.ecrire reçoit les lignes Ma gamme sans prix ni calcul. */
export const PRIX_RESERVE = 'Prix réservé à la direction';
/** 4e passe (B2) : libellé affiché à la place des totaux d'une révision qui contient du Ma gamme. */
export const TOTAUX_RESERVES = 'Totaux réservés (Ma gamme)';
/** Donne à chaque ligne servie sans `calcul` un calcul vide marqué « prix réservé », pour que l'écran s'affiche sans rien inventer. */
export function normaliserVue(v: VueRevision): VueRevision {
  if (v.lignes.every((l) => l.calcul)) return v;
  return { ...v, lignes: v.lignes.map((l) => (l.calcul ? l
    : { ...l, prix_reserve: true, calcul: { id: l.id, complet: true, a_renseigner: [], prix_unitaire_ht: null, total_ht: null } })) };
}
/** Proposition de mise à jour d'une ligne (jamais appliquée sans clic). */
export interface PropositionPrix {
  price_item_id: string; date: string | null; prix: string; fournisseur: string | null; source_libelle: string; motif: 'plus_recent' | 'corrige';
  /** Option tarification avancée : variation depuis le prix du devis (en %) et dépassement du seuil réglé. */
  variation_pct?: string | null; au_dela_seuil?: boolean;
}
export interface AlertePrixLigne { ancien: boolean; age_mois: number | null; proposition: PropositionPrix | null; nb_fournisseurs: number }
export interface AlertesPrix { seuil_mois: number; lignes: Record<string, AlertePrixLigne>; seuil_variation_pct?: string }
export interface Bloc {
  ht_brut: string; remise: string; ht_net: string; tva: string; ttc: string;
  /** Remise sur les seules lignes Ma gamme (migration 053) : leur total HT, assiette de la remise. */
  remise_base?: string;
  /** null : une ligne à prix convenu a un coût inconnu — coût total et marge inconnus. */
  cout_revient?: string | null; marge?: string | null; marge_pct?: string | null; majoration_pct?: string | null;
  reel?: { cout_revient: string | null; marge: string | null; marge_pct: string | null; majoration_pct: string | null };
  /** La révision contient une ligne Ma gamme et ce compte n'a pas l'accès achats : coût de revient et marge réservés. */
  achats_reserves?: boolean;
  /** Avec `achats_reserves` : prix de vente, coût et marge des seules lignes sans produit Ma gamme, avant remise. */
  hors_gamme?: HorsGamme | null;
}
export interface HorsGamme { libelle: string; nb_lignes: number; ht: string; cout_revient: string | null; marge: string | null; marge_pct: string | null }
export interface Totaux {
  complet: boolean; nb_lignes: number; nb_a_renseigner: number; lignes_a_renseigner: { id: string; label: string; motifs: string[] }[];
  mode?: 'marge' | 'majoration'; taux_defaut?: string; remise_pct?: string; taux_tva: string;
  /** 4e passe (B2) : révision avec du Ma gamme, compte qui ne voit pas ses prix (technicien) : aucun total ni remise servis. */
  totaux_reserves?: boolean;
  /** Présent quand la remise porte sur les seules lignes Ma gamme (remise proposée au dirigeant, 31/10/2026). */
  remise_assiette?: 'ma_gamme';
  totaux: Bloc | null; partiel: (Bloc & { libelle: string }) | null;
}
export interface DroitsDevis { couts: boolean; ecrire: boolean; valider: boolean; emettre: boolean; derogation: boolean; prix: boolean; accepter?: boolean; preparer_chantier?: boolean;
  /** Panneau « Remise proposée » (dirigeant seul, option Ma gamme active). */
  remise_proposee?: boolean;
  /** Ma gamme : accorder une baisse sous le tarif (gamme.achats, décision du 01/11/2026). */
  accord_direction?: boolean }
export interface RevisionResume { id: string; rev_no: number; variant: string; status: string; emitted_at: string | null }
export interface VueRevision {
  revision: {
    id: string; quote_id: string; rev_no: number; variant: string; status: string; pricing_mode?: 'marge' | 'majoration'; default_rate?: string;
    /** Absent (4e passe, B2) quand les totaux sont réservés : la remise n'est pas servie au technicien sur un devis Ma gamme. */
    discount_pct?: string; vat_rate: string; hypotheses: string | null; exclusions: string | null; validity_days: number;
    /** Assiette de la remise (migration 053) : tout le devis, ou les lignes Ma gamme. */
    discount_scope?: 'devis' | 'ma_gamme';
    validated_at: string | null; validated_by_name: string | null; emitted_at: string | null; emitted_by_name: string | null;
    created_at: string; updated_at: string; fige: boolean;
  };
  devis: { id: string; number: string; title: string; dossier_id: string };
  dossier: { id: string; reference: string; title: string; demo: boolean };
  lignes: Ligne[];
  totaux: Totaux;
  /** 4e passe (B2) : totaux réservés (Ma gamme, compte qui ne voit pas les prix). */
  totaux_reserves?: boolean;
  derogation: { motif: string; auteur: string; date: string; lignes: { id: string; label: string; motifs: string[] }[] } | null;
  /** « Acceptée par le client » (null = non acceptée). */
  acceptation?: import('./acceptation.tsx').Acceptation | null;
  /** Chantier né de ce devis et de cette variante (null = aucun). */
  chantier?: { id: string; reference: string | null; status: string; depuis_cette_revision: boolean } | null;
  revisions: RevisionResume[];
  /** Remise et Ma gamme (révision non figée) ; null sinon. */
  remise_gamme?: RemiseGamme | null;
  droits: DroitsDevis;
  taux_horaires: { code: string; label: string; hourly_cost: string | null; active: boolean }[];
  /** Alertes de prix (révision non figée, rôle qui voit les coûts) ; null sinon. */
  alertes_prix?: AlertesPrix | null;
  /** Option tarification avancée : articles à prix volatil et validité conseillée (rien n'est modifié seul). */
  conseil_validite?: { jours: number; validite_actuelle: number; depasse: boolean; articles: string[]; nb: number };
  /** Administrateur seulement, option active : coefficients appliqués aux coûts affichés. */
  marge_cachee?: { materiel: string; mo: string };
}

/** Variation signée « +15,2 % » / « −3,1 % ». */
export const variationFr = (v: string) => {
  const n = Number(v);
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
};

/** Libellé du bouton de mise à jour : la date du tarif proposé, toujours visible avant le clic. */
export const libelleMiseAJour = (p: PropositionPrix) =>
  `Mettre à jour au tarif du ${p.date ? date(p.date) : 'date inconnue'}${p.motif === 'corrige' ? ' (corrigé)' : ''}`;

/** Badges d'alerte d'une ligne : prix ancien, tarif plus récent ou corrigé. */
export function BadgesAlertePrix({ a, seuil }: { a?: AlertePrixLigne; seuil: number }) {
  if (!a || (!a.ancien && !a.proposition)) return null;
  return (
    <span className="devis-alertes-prix">
      {a.ancien && <Badge ton="ambre" titre={`Prix daté de plus de ${seuil} mois`}>{`Prix de ${a.age_mois ?? seuil} mois`}</Badge>}
      {a.proposition && <Badge ton="info" titre={a.proposition.source_libelle}>
        {a.proposition.motif === 'corrige' ? 'Prix source corrigé' : `Tarif du ${a.proposition.date ? date(a.proposition.date) : '?'} disponible`}
      </Badge>}
      {a.proposition?.variation_pct && <Badge ton={a.proposition.au_dela_seuil ? 'ambre' : 'neutre'} titre="Variation depuis le prix du devis">
        {`${variationFr(a.proposition.variation_pct)} depuis le prix du devis`}</Badge>}
    </span>
  );
}

/** Montant : euros() ; inconnu → badge ambre « À renseigner », jamais 0. */
export function Montant({ v, fort }: { v: string | null | undefined; fort?: boolean }) {
  const t = euros(v);
  if (t === null) return <BadgeARenseigner />;
  return <span className={`chiffre ${fort ? 'devis-fort' : ''}`}>{t}</span>;
}

export function BadgeARenseigner({ titre }: { titre?: string }) {
  return <Badge ton="ambre" icone={<AlertTriangle size={14} aria-hidden />} titre={titre}>À renseigner</Badge>;
}

export const pct = (v: string | null | undefined) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`);
export const quantite = (v: string) => Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 4 });
/** Heures : null = temps de pose à renseigner (jamais affiché 0). */
export const heures = (v: string | null | undefined) => (v === null || v === undefined ? 'À renseigner' : `${quantite(v)} h`);
/** Chaîne décimale serveur → saisie française (« 12.5000 » → « 12,5 »). */
export const versSaisie = (v: string | null | undefined) => (v === null || v === undefined ? '' : String(Number(v)).replace('.', ','));
/** La saisie est-elle un nombre complet (sinon on attend la fin de la frappe) ? */
export const saisieValide = (s: string) => s.trim() === '' || /^-?[\d\s  ]*([.,]\d+)?\s*€?$/.test(s.trim()) && /\d/.test(s);
export const sourcePrix = (l: { cost_source?: string | null; cost_date?: string | null }) =>
  [l.cost_source, l.cost_date ? date(l.cost_date) : null].filter(Boolean).join(' — ');

/** Champ de saisie qui garde la frappe en cours même si les données serveur se rafraîchissent, et enregistre
 *  à la sortie du champ (ou après une pause de frappe). */
export function SaisieAuto({ valeur, enregistrer, montantFr, ...p }:
  { valeur: string; enregistrer: (v: string) => Promise<unknown> | void; montantFr?: boolean } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [local, setLocal] = useState(valeur);
  const [erreur, setErreur] = useState(false);
  const focus = useRef(false);
  const minuterie = useRef<number | undefined>(undefined);
  useEffect(() => { if (!focus.current) setLocal(valeur); }, [valeur]);
  const valider = async (v: string) => {
    window.clearTimeout(minuterie.current);
    if (v === valeur) { setErreur(false); return; }
    if (montantFr && !saisieValide(v)) { setErreur(true); return; }
    try { await enregistrer(v); setErreur(false); } catch { setErreur(true); }
  };
  return (
    <input {...p} className={`saisie ${p.className ?? ''}`} value={local} aria-invalid={erreur || undefined}
      inputMode={montantFr ? 'decimal' : p.inputMode}
      onFocus={() => { focus.current = true; }}
      onBlur={() => { focus.current = false; void valider(local); }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setLocal(valeur); setErreur(false); } }}
      onChange={(e) => {
        const v = e.target.value; setLocal(v);
        window.clearTimeout(minuterie.current);
        if (!montantFr || saisieValide(v)) minuterie.current = window.setTimeout(() => { void valider(v); }, 1200);
      }} />
  );
}

/** Vrai sous une largeur donnée (bascule tableau ↔ cartes sans dupliquer les champs dans la page). */
export function useEtroit(px = 900) {
  const requete = `(max-width: ${px}px)`;
  return useSyncExternalStore(
    (cb) => { const mq = window.matchMedia(requete); mq.addEventListener('change', cb); return () => mq.removeEventListener('change', cb); },
    () => window.matchMedia(requete).matches,
    () => false,
  );
}
